import React, { useState, useEffect } from 'react';
import './App.css';
import GenerateButton from './GenerateButton';
import { fetchOptions } from './api';
import generateStartup from './api';
import {
  initGA,
  checkGA,
  checkEvents,
  sendTestEvent,
  trackOptionSelection,
  trackStartupGeneration,
  trackPopupView,
  trackCTAClick,
  trackError,
  trackPopupClose
} from './utils/analytics';
import {
  getSavedLanguage,
  saveLanguage,
  getTranslations,
  getTranslatedCategories,
  languages
} from './i18n';

function App() {
  const [startup, setStartup] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState({ ramoDeAtividade: [], caracteristicas: [] });
  const [selectedRamo, setSelectedRamo] = useState([]);
  const [selectedCaracteristicas, setSelectedCaracteristicas] = useState([]);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showDebugTools, setShowDebugTools] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(getSavedLanguage());
  const translations = getTranslations(currentLanguage);
  const translatedCategories = getTranslatedCategories(options, currentLanguage);
  const urlLogoDefault = './images/mock_logo.png';

  useEffect(() => {
    // Check for debug parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get('debug') === 'true';
    setShowDebugTools(debugMode);
    
    // Initialize Google Analytics
    initGA('G-HYB7VB9MPK');
    
    // Check if Google Analytics is working
    const isGAWorking = checkGA();
    console.log('Is Google Analytics working?', isGAWorking);
    
    // Check if events are being sent correctly
    if (isGAWorking && debugMode) {
      checkEvents();
    }
    
    const loadOptions = async () => {
      try {
        const data = await fetchOptions();
        setOptions(data);
      } catch (error) {
        setError(translations.errors.loadOptions);
        trackError('load_options', error.message);
      }
    };
    loadOptions();
  }, [translations.errors.loadOptions]);

  // Simple popup state management
  useEffect(() => {
    if (startup && !showPopup) {
      setShowPopup(true);
      trackPopupView(startup.name);
    }
  }, [startup]);

  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
    saveLanguage(language);
  };

  const handleRamoSelect = (ramo) => {
    setSelectedRamo(prev => {
      if (prev.includes(ramo)) {
        return prev.filter(r => r !== ramo);
      }
      if (prev.length >= 3) {
        alert(translations.businessSectors.maxSelectionAlert);
        return prev;
      }
      return [...prev, ramo];
    });
    trackOptionSelection('business_sectors', ramo, !selectedRamo.includes(ramo));
  };

  const handleCaracteristicaSelect = (caracteristica) => {
    setSelectedCaracteristicas(prev => {
      if (prev.includes(caracteristica)) {
        return prev.filter(c => c !== caracteristica);
      }
      if (prev.length >= 3) {
        alert(translations.characteristics.maxSelectionAlert);
        return prev;
      }
      return [...prev, caracteristica];
    });
    trackOptionSelection('characteristics', caracteristica, !selectedCaracteristicas.includes(caracteristica));
  };

  const handleGenerate = async () => {
    if (selectedRamo.length === 0 || selectedCaracteristicas.length === 0) {
      setError(translations.errors.minSelection);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const result = await generateStartup({
        ramoDeAtividade: selectedRamo,
        caracteristicas: selectedCaracteristicas,
        language: currentLanguage
      });
      
      setStartup(result);
      trackStartupGeneration(selectedRamo, selectedCaracteristicas);
    } catch (error) {
      console.error('Error generating startup:', error);
      setError(translations.errors.generateFailed);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosePopup = () => {
    if (startup) {
      trackPopupClose(startup.name);
    }
    setShowPopup(false);
  };

  const handleCTAClick = () => {
    if (startup) {
      trackCTAClick(startup.name);
      const url = startup.ctaLink || 'https://www.google.com';
      window.open(url, '_blank');
    }
  };

  const getPopupStyle = () => {
    if (!startup?.colorTheme) return {};
    
    return {
      '--popup-bg': startup.colorTheme.background || '#1a1c20',
      '--text-color': startup.colorTheme.text || '#ffffff',
      '--accent-color': startup.colorTheme.accent || '#4ecdc4',
      '--card-bg': startup.colorTheme.card || '#2d2f36'
    };
  };

  const getLogo = (url) => {
    if (!url || !url.includes('http')) {
      return urlLogoDefault;
    }
    return url;
  };

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="App">
      <div className="centered-content">
        <div className="language-selector">
          {Object.entries(languages).map(([code, lang]) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code)}
              className={`language-button ${currentLanguage === code ? 'selected' : ''}`}
            >
              {lang.language}
            </button>
          ))}
        </div>

        <h1>🚀 AI Startup Generator</h1>
        
        {showDebugTools && (
          <div style={{ 
            position: 'absolute', 
            top: '10px', 
            right: '10px', 
            fontSize: '12px', 
            color: '#4ecdc4', 
            opacity: 0.7 
          }}>
            {translations.debug.mode}
          </div>
        )}
        
        {showDebugTools && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <button 
              onClick={sendTestEvent}
              style={{ 
                padding: '5px 10px', 
                backgroundColor: '#4ecdc4', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {translations.buttons.testGA}
            </button>
            
            <button 
              onClick={checkEvents}
              style={{ 
                padding: '5px 10px', 
                backgroundColor: '#4ecdc4', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {translations.buttons.checkEvents}
            </button>
          </div>
        )}
        
        <div className="options-container">
          <div className="options-section">
            <h2>{translations.businessSectors.title}</h2>
            <div className="options-grid">
              {options.ramoDeAtividade.map(ramo => (
                <button
                  key={ramo}
                  onClick={() => handleRamoSelect(ramo)}
                  className={`option-button ${selectedRamo.includes(ramo) ? 'selected' : ''}`}
                >
                  {translatedCategories.businessSectors[ramo] || ramo}
                </button>
              ))}
            </div>
            <p className="selection-count">
              {translations.businessSectors.selected}: {selectedRamo.length}/3
            </p>
          </div>

          <div className="options-section">
            <h2>{translations.characteristics.title}</h2>
            <div className="options-grid">
              {options.caracteristicas.map(caracteristica => (
                <button
                  key={caracteristica}
                  onClick={() => handleCaracteristicaSelect(caracteristica)}
                  className={`option-button ${selectedCaracteristicas.includes(caracteristica) ? 'selected' : ''}`}
                >
                  {translatedCategories.characteristics[caracteristica] || caracteristica}
                </button>
              ))}
            </div>
            <p className="selection-count">
              {translations.characteristics.selected}: {selectedCaracteristicas.length}/3
            </p>
          </div>

          <GenerateButton
            onClick={handleGenerate}
            isLoading={isLoading}
            disabled={selectedRamo.length === 0 || selectedCaracteristicas.length === 0}
          />
        </div>
      </div>

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading">{translations.loading.generating}</div>
        </div>
      )}

      {showPopup && startup && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <div 
            className="popup"
            style={getPopupStyle()}
            onClick={e => e.stopPropagation()}
          >
            <button className="close-button" onClick={handleClosePopup}>×</button>
            
            <div className="popup-header">
              <img 
                src={getLogo(startup.logo)} 
                alt={`${startup.name} logo`} 
                className="popup-logo"
              />
              <h2 className="popup-title">{startup.name}</h2>
            </div>

            <div className="popup-content">
              <h1 className="title">{startup.landing?.title}</h1>
              <p className="subtitle">{startup.landing?.subtitle}</p>

              <div className="card-section">
                {startup.landing?.sections?.map((section, index) => (
                  <div key={index} className="card">
                    <h4>{section.title}</h4>
                    <p>{section.description}</p>
                  </div>
                ))}
              </div>

              <button 
                className="cta-button"
                onClick={() => handleCTAClick(startup)}
              >
                {startup.landing?.cta}
              </button>
            </div>
          </div>
        </div>
      )}
      <footer className="footer">
        <div className="footer-content">
          <p>Part of the AI Forge Code initiative</p>
          <div className="social-links">
            <a href="https://www.aiforgecode.com/" target="_blank" rel="noopener noreferrer" title="AI Forge Code Website">
              <span className="social-icon">🌐</span>
            </a>
            <a href="https://twitter.com/aiforgecode" target="_blank" rel="noopener noreferrer" title="Follow us on X (Twitter)">
              <span className="social-icon">🐦</span>
            </a>
            <a href="https://instagram.com/aiforgecode" target="_blank" rel="noopener noreferrer" title="Follow us on Instagram">
              <span className="social-icon">📸</span>
            </a>
            <a href="https://discord.gg/33aF4xrSV2" target="_blank" rel="noopener noreferrer" title="Join our Discord">
              <span className="social-icon">💬</span>
            </a>
            <a href="https://www.youtube.com/@AiForgeCode" target="_blank" rel="noopener noreferrer" title="Subscribe to our YouTube">
              <span className="social-icon">📺</span>
            </a>
            <a href="https://linktr.ee/aiforgecode" target="_blank" rel="noopener noreferrer" title="All our links">
              <span className="social-icon">🔗</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
